/**
 * Hook useShipping
 * 
 * Gerencia cálculo de frete
 */

import { useState, useCallback } from 'react';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { handleError } from '../utils/errorHandler';
import { isValidCEP } from '../utils/validators';

/**
 * @param {Array} cartItems - Itens do carrinho
 * @param {string} cepOrigin - CEP de origem (padrão: "25260160")
 * @returns {Object} { calculateShipping, selectedShipping, shippingCost, loading, error }
 */
export const useShipping = (cartItems = [], cepOrigin = '25260160') => {
  const [selectedShipping, setSelectedShipping] = useState(null);
  const [shippingCost, setShippingCost] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const calculateShipping = useCallback(async (cepDestination) => {
    // Valida CEP
    if (!isValidCEP(cepDestination)) {
      const errorMessage = 'Por favor, informe um CEP válido (ex: 12345-678 ou 12345678).';
      setError(errorMessage);
      handleError(
        { message: 'CEP inválido', type: 'validation' },
        { context: 'useShipping', customMessage: errorMessage }
      );
      return;
    }

    if (!cartItems || cartItems.length === 0) {
      setError('Adicione produtos ao carrinho para calcular o frete.');
      return;
    }

    setLoading(true);
    setError(null);
    setSelectedShipping(null);
    setShippingCost(0);

    try {
      const response = await fetchWithAuth(
        `${process.env.REACT_APP_BACKEND_URL}/api/melhorenvio/calculate-freight`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            cepOrigin,
            cepDestination: cepDestination.replace(/\D/g, ''), // Remove formatação
            products: cartItems.map((item) => ({
              id: item.id,
              weight: item.weight || 1,
              height: item.height || 10,
              width: item.width || 30,
              length: item.length || 50,
              quantity: item.quantity,
              insurance_value: Number(item.price || 0),
            })),
          }),
        }
      );

      const data = await response.json();
      let chosenShippingOption = null;

      if (data && Array.isArray(data.data)) {
        chosenShippingOption = data.data.find(option => option.id === 2) || data.data[0];
      } else if (data?.data && typeof data.data === 'object') {
        chosenShippingOption = data.data.id === 2 ? data.data : data.data;
      }

      if (chosenShippingOption && chosenShippingOption.price) {
        setSelectedShipping(chosenShippingOption);
        setShippingCost(parseFloat(chosenShippingOption.price));
        setError(null);
      } else {
        const errorMessage = 'Nenhuma opção de frete disponível para o CEP informado. Verifique o CEP e tente novamente.';
        setError(errorMessage);
        handleError(
          { message: 'Frete não disponível' },
          { context: 'useShipping', customMessage: errorMessage }
        );
      }
    } catch (err) {
      const errorMessage = 'Erro ao calcular o frete. Verifique o CEP e tente novamente.';
      setError(errorMessage);
      handleError(err, {
        context: 'useShipping',
        customMessage: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  }, [cartItems, cepOrigin]);

  const resetShipping = useCallback(() => {
    setSelectedShipping(null);
    setShippingCost(0);
    setError(null);
  }, []);

  return {
    calculateShipping,
    selectedShipping,
    shippingCost,
    loading,
    error,
    resetShipping,
  };
};

export default useShipping;

